import { PaginationState, SortingState } from '@tanstack/react-table';
import { Link } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkTrackingDto } from '@/Domain/api/Analytics/dto/LinkTracking.dto';
import { LinkTrackingTable } from '@/Domain/components/Insights/LinkTracking/LinkTrackingTable';
import { Card, CardHeader, CardDescription, CardTitle } from '@/Domain/components/Shared/Card/Card';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

type Props = {
  data: LinkTrackingDto | undefined;
  isLoading: boolean;
  isError: boolean;
  isLinkTrackingActive: boolean;
  onToggleLinkTracking: (check: boolean) => void;
  sorting: SortingState;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export const LinkTrackingSection = ({
  data,
  isLoading,
  isError,
  isLinkTrackingActive,
  onToggleLinkTracking,
  sorting,
  pagination,
  setPagination,
  setSorting,
}: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.link_tracking' });

  const totalConversion = useMemo(() => {
    if (!data?.total_generation_count) return 0;
    return (data.total_click_count / data.total_generation_count) * 100;
  }, [data?.total_generation_count, data?.total_click_count]);

  return (
    <>
      <Item variant="muted" className="w-full" size="sm">
        <ItemMedia variant="icon">
          <Link className="size-7 text-foreground-subtle" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle className="line-clamp-1">{t('title')}</ItemTitle>
          <ItemDescription>{t('description')}</ItemDescription>
        </ItemContent>
        <ItemActions className="shrink-0">
          <Switch defaultChecked={isLinkTrackingActive} onCheckedChange={onToggleLinkTracking} />
        </ItemActions>
      </Item>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <div className="flex items-center justify-between pb-2">
            <CardHeader>
              <CardTitle>{t('top_links')}</CardTitle>
              <CardDescription>{t('top_links_description')}</CardDescription>
            </CardHeader>
            <div className="!hidden lg:!grid lg:grid-cols-3 lg:gap-0 lg:-mt-3">
              <div className="flex flex-col space-y-1.5 items-center justify-center px-7 py-4 rounded-bl-lg bg-muted text-foreground">
                <span className="text-xs font-medium text-foreground-subtle text-center">
                  {t('total_sent')}
                </span>
                <span className="text-xl font-semibold text-muted-foreground">
                  {data?.total_generation_count ?? 0}
                </span>
              </div>
              <div className="flex flex-col space-y-1.5 items-center justify-center px-7 py-4 bg-green-100 text-foreground">
                <span className="text-xs font-medium text-green-600 text-center">
                  {t('total_clicked')}
                </span>
                <span className="text-xl font-semibold text-green-700">
                  {data?.total_click_count ?? 0}
                </span>
              </div>
              <div className="flex flex-col space-y-1.5 items-center justify-center px-7 py-4 bg-green-200 text-foreground rounded-tr-xl">
                <span className="text-xs font-medium text-green-800 text-center">
                  {t('total_conversion')}
                </span>
                <span className="text-xl font-semibold text-green-900">
                  {totalConversion.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>

          <LinkTrackingTable
            data={data}
            isLinkTrackingActive={!!isLinkTrackingActive}
            isError={isError}
            isLoading={isLoading}
            sorting={sorting}
            setSorting={setSorting}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Card>
      </div>
    </>
  );
};
